import { useEffect } from 'react';
import axios from 'axios';
import { createDocValueStore } from './docStore';
import { readState, writeState } from './docState';
import { forgetShared } from './sharedGet';
import { useSubmodels, type Submodel } from './listingStore';
import { displayVariantLabel, variantLabel } from './variantLabel';
import { VIEW_DIRECTIONS } from './viewDirections';
import { IS_LOCKED } from '../../apiBase';

// EVERY LISTING PHOTOGRAPH OF A KNOWN SIDE, SAVED ON THE SERVER WITHOUT BEING ASKED.
//
// The materials and the meshes read only the photographs the server holds (notice_listing_images),
// and those used to get there one switch at a time, under each picture of the Web listings
// gallery. A manual whose listing was read and never switched had none, and Phase 3's Run was
// grey with nothing to say why. Now each colourway's pictures that Phase 1 placed on a side of
// the product (front, back, 45°…: see VIEW_DIRECTIONS) are saved as soon as they are placed,
// and the switch is for taking one out.
//
// ONCE PER PICTURE: what was saved is remembered on the manual, so a picture switched off stays
// off. And RETROACTIVE: a manual opened with pictures placed before this existed saves them then.
const savedKey = (docId: string) => `notice.listingPhotosSaved.${docId}`;

// Bumped once a save lands, so whatever lists the stored photographs reads them again.
const epoch = createDocValueStore<number>(0);
export const useStoredPhotosEpoch = epoch.useValue;

const VIEWS = new Set<string>(VIEW_DIRECTIONS.map((v) => v.id));
const inFlight = new Set<string>();

async function savePlaced(docId: string, s: Submodel): Promise<boolean> {
  const saved = new Set(readState<string[]>(docId, savedKey(docId), []));
  const urls = s.gallery.filter((url, i) => VIEWS.has(s.galleryViews[i] ?? '') && !saved.has(url));
  if (!urls.length) return false;
  const { data } = await axios.post<{ held?: string[] }>('/api/notice/listing-images', {
    docId,
    urls,
    variant: s.id,
    variantLabel: displayVariantLabel(variantLabel(s)),
    dimensioned: urls.filter((url) => s.galleryDimensioned[s.gallery.indexOf(url)]),
  });
  // ONLY WHAT THE SERVER NOW HOLDS: a picture it could not download is tried again next time.
  // Re-read: another colourway's save may have landed meanwhile.
  const held = data.held ?? [];
  if (!held.length) return false;
  const now = new Set(readState<string[]>(docId, savedKey(docId), []));
  for (const url of held) now.add(url);
  writeState(docId, savedKey(docId), [...now]);
  return true;
}

export function useSaveListingPhotos(docId: string): void {
  const submodels = useSubmodels(docId);
  useEffect(() => {
    if (!docId || IS_LOCKED) return;
    for (const s of submodels) {
      // Not while it is being read: the strip is about to change under it.
      if (s.detecting || s.pending) continue;
      const key = `${docId}\x1f${s.id}`;
      if (inFlight.has(key)) continue;
      inFlight.add(key);
      void savePlaced(docId, s)
        .then((did) => {
          if (!did) return;
          // The other colourways' saves may still be out: a list read for the first must not
          // stand in for the last.
          forgetShared();
          epoch.set(docId, epoch.get(docId) + 1);
        })
        .catch((e) => console.warn('[listing-photos] save failed', s.id, e))
        .finally(() => inFlight.delete(key));
    }
  }, [docId, submodels]);
}
